import React from "react";
import type { FormPage, FormSection, FormField } from "../types";
import FieldConfigModal from "./FieldConfigModal";
import { StepHeader } from "./StepHeader";
import { SectionDetails } from "./SectionDetails";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SectionHeader from "./SectionHeader";
import AddSectionDivider from "./AddSectionDivider";

interface SectionDnDRowProps {
  section: FormSection;
  idx: number;
  isDraggingSection: boolean;
  pages: FormPage[];
  updateSection: (id: string, updates: Partial<FormSection>) => void;
  openFieldModal: (type: string, sectionId: string) => void;
  deleteField: (sectionId: string, fieldId: string) => void;
  handleUpdateField: (sectionId: string, updatedField: FormField) => void;
  reorderFields: (sectionId: string, newFields: FormField[]) => void;
  setSectionToDelete: (id: string | null) => void;
  setShowDeleteSectionModal: (show: boolean) => void;
  moreThanOne: boolean;
  listeners?: React.HTMLAttributes<HTMLElement>;
  attributes?: React.HTMLAttributes<HTMLElement>;
}

const SectionDnDRow: React.FC<SectionDnDRowProps> = ({
  section,
  idx,
  isDraggingSection,
  pages,
  updateSection,
  openFieldModal,
  deleteField,
  handleUpdateField,
  reorderFields,
  setSectionToDelete,
  setShowDeleteSectionModal,
  moreThanOne,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    opacity: isDragging ? 0.8 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <SectionDetails
        formData={pages}
        section={section}
        sectionIndex={idx}
        onChange={(id, updates) => updateSection(id, updates)}
        onAddField={(type) => openFieldModal(type, section.id)}
        onDeleteField={deleteField}
        onUpdateField={handleUpdateField}
        onReorderFields={reorderFields}
        isDraggingSection={isDraggingSection}
        showGrip={moreThanOne}
        showTrash={moreThanOne}
        onDelete={() => {
          setSectionToDelete(section.id);
          setShowDeleteSectionModal(true);
        }}
        gripProps={moreThanOne ? { ...listeners, ...attributes } : {}}
      />
    </div>
  );
};

interface FormDesignFormProps {
  pages: FormPage[];
  setPages: React.Dispatch<React.SetStateAction<FormPage[]>>;
  selectedPageId: string;
  setSelectedPageId: (id: string) => void;
  isDraggingSection: boolean;
  setIsDraggingSection: (drag: boolean) => void;
  showDeleteStepModal: boolean;
  setShowDeleteStepModal: (show: boolean) => void;
  showDeleteSectionModal: boolean;
  setShowDeleteSectionModal: (show: boolean) => void;
  setSectionToDelete: (id: string | null) => void;
  onRenameStep: (stepId: string) => void;
  onReorderStep: () => void;
  onDeleteStep: (stepId: string) => void;
  onAddStep: () => void;
}

export const ContentForm: React.FC<FormDesignFormProps> = ({
  pages,
  setPages,
  selectedPageId,
  setSelectedPageId,
  isDraggingSection,
  setIsDraggingSection,
  setShowDeleteSectionModal,
  setSectionToDelete,
  onRenameStep,
  onReorderStep,
  onDeleteStep,
  onAddStep,
}) => {
  // Helper functions
  const getCurrentPage = () => pages.find((p) => p.id === selectedPageId);

  // Section management
  const addSection = () => {
    const currentPage = getCurrentPage();
    if (!currentPage) return;
    const newSection: FormSection = {
      id: `section_${Date.now()}`,
      name: "",
      technicalKey: `seccion_${currentPage.sections.length + 1}`,
      description: "",
      fields: [],
    };
    setPages(
      pages.map((p) =>
        p.id === selectedPageId
          ? { ...p, sections: [...p.sections, newSection] }
          : p,
      ),
    );
    setIsDraggingSection(false); // Ensure button is visible after adding
  };

  const updateSection = (sectionId: string, updates: Partial<FormSection>) => {
    setPages(
      pages.map((p) => ({
        ...p,
        sections: p.sections.map((s) =>
          s.id === sectionId ? { ...s, ...updates } : s,
        ),
      })),
    );
  };

  // Field modal state
  const [showFieldModal, setShowFieldModal] = React.useState(false);
  const [fieldModalType, setFieldModalType] = React.useState<string | null>(
    null,
  );
  const [fieldModalSectionId, setFieldModalSectionId] = React.useState<
    string | null
  >(null);

  const openFieldModal = (type: string, sectionId: string) => {
    setFieldModalType(type);
    setFieldModalSectionId(sectionId);
    setShowFieldModal(true);
  };
  const closeFieldModal = () => {
    setShowFieldModal(false);
    setFieldModalType(null);
    setFieldModalSectionId(null);
  };

  const handleFieldModalSave = (data: FormField) => {
    // Add the new field to the correct section
    setPages((pages) =>
      pages.map((page) =>
        page.id === selectedPageId
          ? {
              ...page,
              sections: page.sections.map((section) =>
                section.id === fieldModalSectionId
                  ? { ...section, fields: [...section.fields, data] }
                  : section,
              ),
            }
          : page,
      ),
    );
    closeFieldModal();
  };

  const handleUpdateField = (sectionId: string, updatedField: FormField) => {
    setPages((pages) =>
      pages.map((page) =>
        page.id === selectedPageId
          ? {
              ...page,
              sections: page.sections.map((section) =>
                section.id === sectionId
                  ? {
                      ...section,
                      fields: section.fields.map((f) =>
                        f.id === updatedField.id ? updatedField : f,
                      ),
                    }
                  : section,
              ),
            }
          : page,
      ),
    );
  };

  const deleteField = (sectionId: string, fieldId: string) => {
    setPages((pages) =>
      pages.map((page) =>
        page.id === selectedPageId
          ? {
              ...page,
              sections: page.sections.map((section) =>
                section.id === sectionId
                  ? {
                      ...section,
                      fields: section.fields.filter(
                        (field) => field.id !== fieldId,
                      ),
                    }
                  : section,
              ),
            }
          : page,
      ),
    );
  };

  const reorderFields = (sectionId: string, newFields: FormField[]) => {
    setPages((pages) =>
      pages.map((page) =>
        page.id === selectedPageId
          ? {
              ...page,
              sections: page.sections.map((section) =>
                section.id === sectionId
                  ? {
                      ...section,
                      fields: newFields,
                    }
                  : section,
              ),
            }
          : page,
      ),
    );
  };

  const renderSections = () => {
    const currentPage = getCurrentPage();
    if (!currentPage) return null;
    if (!currentPage.sections || currentPage.sections.length === 0) {
      return (
        <div className="p-4 text-body-quiet">
          Este paso no tiene secciones. Añade una sección para continuar.
        </div>
      );
    }
    const moreThanOne = currentPage.sections.length > 1;
    if (moreThanOne) {
      return (
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={() => setIsDraggingSection(true)}
          onDragEnd={(event: DragEndEvent) => {
            setIsDraggingSection(false);
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const oldIndex = currentPage.sections.findIndex(
              (s) => s.id === active.id,
            );
            const newIndex = currentPage.sections.findIndex(
              (s) => s.id === over.id,
            );
            if (oldIndex === -1 || newIndex === -1) return;
            const newSections = arrayMove(
              currentPage.sections,
              oldIndex,
              newIndex,
            );
            setPages(
              pages.map((p) => {
                return p.id === selectedPageId
                  ? {
                      ...p,
                      sections: newSections.map(
                        (s, sectionIndex, sectionArr) => {
                          return {
                            ...s,
                            fields: s.fields.map((f) => {
                              if (!f.conditions) return f;
                              const conditions = f.conditions.filter((cond) => {
                                const condVarId = cond?.data?.selectedVar?.id;
                                const fieldSectionIndex = sectionArr.findIndex(
                                  (sec) => {
                                    return sec.fields.some(
                                      (field) =>
                                        (field.technicalKey || field.name) ===
                                        condVarId,
                                    );
                                  },
                                );
                                if (fieldSectionIndex === -1) return false;
                                if (fieldSectionIndex > sectionIndex)
                                  return false;
                                return true;
                              });
                              return {
                                ...f,
                                conditions,
                                isConditional: conditions.length > 0,
                              };
                            }),
                          };
                        },
                      ),
                    }
                  : p;
              }),
            );
          }}
        >
          <SortableContext items={currentPage.sections.map((s) => s.id)}>
            <div className={"py-4"}>
              {currentPage.sections.map((section, idx) => (
                <div key={section.id}>
                  {/* No divider/title row here when moreThanOne, SectionDetails handles it */}
                  <SectionDnDRow
                    section={section}
                    idx={idx}
                    isDraggingSection={isDraggingSection}
                    pages={pages}
                    updateSection={updateSection}
                    openFieldModal={openFieldModal}
                    deleteField={deleteField}
                    handleUpdateField={handleUpdateField}
                    reorderFields={reorderFields}
                    setSectionToDelete={setSectionToDelete}
                    setShowDeleteSectionModal={setShowDeleteSectionModal}
                    moreThanOne={moreThanOne}
                  />
                </div>
              ))}
              {!isDraggingSection && <AddSectionDivider onClick={addSection} />}
            </div>
          </SortableContext>
        </DndContext>
      );
    } else {
      // Only one section, no DnD
      return (
        <div className="space-y-6 py-4">
          {currentPage.sections.map((section, idx) => (
            <div key={section.id}>
              <section
                className="px-2 mt-2 -mb-2"
                onClick={() => {
                  console.log(pages);
                }}
              >
                <SectionHeader />
              </section>
              <SectionDetails
                formData={pages}
                section={section}
                sectionIndex={idx}
                onChange={(id, updates) => updateSection(id, updates)}
                onAddField={(type) => openFieldModal(type, section.id)}
                onDeleteField={deleteField}
                onUpdateField={handleUpdateField}
                onReorderFields={reorderFields}
                isDraggingSection={false}
                showGrip={false}
                showTrash={false}
              />
            </div>
          ))}
          {!isDraggingSection && <AddSectionDivider onClick={addSection} />}
        </div>
      );
    }
  };

  return (
    <div className="h-full flex flex-col">
      <StepHeader
        pages={pages}
        selectedPageId={selectedPageId}
        setSelectedPageId={setSelectedPageId}
        onRenameStep={onRenameStep}
        onReorderStep={onReorderStep}
        onDeleteStep={onDeleteStep}
        onAddStep={onAddStep}
      />
      <div className="flex-1 overflow-y-auto">{renderSections()}</div>
      {showFieldModal && (
        <FieldConfigModal
          formData={pages}
          open={showFieldModal}
          type={fieldModalType}
          sectionId={fieldModalSectionId}
          onClose={closeFieldModal}
          onSave={handleFieldModalSave}
        />
      )}
    </div>
  );
};
